import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Eye, Mail, Trash2, Check, Ban, MessageSquare, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/axios';

const ModerationActions = ({ report, onActionComplete }) => {
  const navigate = useNavigate();
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState('7');
  const [isLoading, setIsLoading] = useState(false);

  // Delete offer
  const handleDeleteOffer = async () => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer l'offre "${report.offer_title}" ?\n\nCette action est irréversible.`
      )
    ) {
      try {
        setIsLoading(true);
        await api.delete(`/offers/${report.offer_id}`);
        toast.success('Offre supprimée');
        
        // Send email notification to creator
        if (report.offer_creator_email) {
          try {
            await api.post('/notifications/report-deleted', {
              email: report.offer_creator_email,
              offer_title: report.offer_title,
              reason: report.reason
            });
          } catch (e) {
            console.error('Failed to send email:', e);
          }
        }
        
        if (onActionComplete) onActionComplete();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Update status
  const handleUpdateStatus = async (status) => {
    try {
      setIsLoading(true);
      await api.patch(`/reports/${report.id}/status`, { status });
      toast.success(
        status === 'reviewed' ? 'Marqué comme examiné' : 'Marqué comme résolu'
      );
      
      // Send email if resolved
      if (status === 'resolved' && report.offer_creator_email) {
        try {
          await api.post('/notifications/report-resolved', {
            email: report.offer_creator_email,
            offer_title: report.offer_title,
            resolution_note: 'Le signalement a été examiné et résolu par notre équipe.'
          });
        } catch (e) {
          console.error('Failed to send email:', e);
        }
      }
      
      if (onActionComplete) onActionComplete();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setIsLoading(false);
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Veuillez saisir une note');
      return;
    }

    try {
      setIsLoading(true);
      await api.post(`/reports/${report.id}/notes`, { note: noteText });
      toast.success('Note ajoutée');
      setNoteText('');
      setShowNoteDialog(false);
      if (onActionComplete) onActionComplete();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la note');
    } finally {
      setIsLoading(false);
    }
  };

  // Ban user
  const handleBanUser = async () => {
    if (!banReason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    try {
      setIsLoading(true);
      await api.post(`/users/${report.offer_creator_id}/ban`, {
        reason: banReason,
        duration_days: parseInt(banDuration)
      });
      
      toast.success(`Utilisateur banni pour ${banDuration} jour(s)`);
      setBanReason('');
      setShowBanDialog(false);
      if (onActionComplete) onActionComplete();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors du bannissement';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex gap-2 flex-wrap">
        {/* View offer */}
        <Button
          onClick={() => navigate(`/offre/${report.offer_id}`)}
          variant="outline"
          size="sm"
          className="border-purple-500 text-purple-600 hover:bg-purple-50"
          disabled={isLoading}
        >
          <Eye className="w-3 h-3 mr-2" />
          Voir l'offre
        </Button>

        {/* Contact creator */}
        {report.offer_creator_id && (
          <Button
            onClick={() =>
              navigate('/messages', {
                state: { openConversationWith: report.offer_creator_id },
              })
            }
            variant="outline"
            size="sm"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            disabled={isLoading}
          >
            <Mail className="w-3 h-3 mr-2" />
            Contacter {report.offer_creator_name}
          </Button>
        )}

        {/* Add note */}
        <Button
          onClick={() => setShowNoteDialog(true)}
          variant="outline"
          size="sm"
          className="border-indigo-500 text-indigo-600 hover:bg-indigo-50"
          disabled={isLoading}
        >
          <MessageSquare className="w-3 h-3 mr-2" />
          Ajouter note
        </Button>

        {/* Ban user */}
        {report.offer_creator_id && (
          <Button
            onClick={() => setShowBanDialog(true)}
            variant="outline"
            size="sm"
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
            disabled={isLoading}
          >
            <Ban className="w-3 h-3 mr-2" />
            Bannir utilisateur
          </Button>
        )}

        {/* Delete offer */}
        <Button
          onClick={handleDeleteOffer}
          variant="outline"
          size="sm"
          className="border-red-500 text-red-600 hover:bg-red-50"
          disabled={isLoading}
        >
          <Trash2 className="w-3 h-3 mr-2" />
          Supprimer l'offre
        </Button>

        {/* Status actions */}
        {report.status === 'pending' && (
          <>
            <Button
              onClick={() => handleUpdateStatus('reviewed')}
              variant="outline"
              size="sm"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
              disabled={isLoading}
            >
              <Check className="w-3 h-3 mr-2" />
              Marquer examiné
            </Button>
            <Button
              onClick={() => handleUpdateStatus('resolved')}
              variant="outline"
              size="sm"
              className="border-green-500 text-green-600 hover:bg-green-50"
              disabled={isLoading}
            >
              <Check className="w-3 h-3 mr-2" />
              Marquer résolu
            </Button>
          </>
        )}
      </div>

      {/* Notes dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une note interne</DialogTitle>
            <DialogDescription>
              Cette note sera visible uniquement par l'équipe de modération
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                placeholder="Écrivez votre note ici..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
              />
            </div>

            {/* Display existing notes */}
            {report.notes && report.notes.length > 0 && (
              <div className="space-y-2">
                <Label>Notes existantes</Label>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {report.notes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-gray-50 p-3 rounded-lg border text-sm"
                    >
                      <p className="text-gray-700 mb-1">{note.text}</p>
                      <p className="text-xs text-gray-500">
                        Par {note.by_name} le{' '}
                        {new Date(note.created_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNoteDialog(false)}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button onClick={handleAddNote} disabled={isLoading}>
              {isLoading ? 'Ajout...' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ban dialog */}
      <Dialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bannir l'utilisateur</DialogTitle>
            <DialogDescription>
              L'utilisateur <strong>{report.offer_creator_name}</strong> sera temporairement suspendu
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ban-reason">Raison du bannissement *</Label>
              <Textarea
                id="ban-reason"
                placeholder="Ex: Contenu inapproprié répété, non-respect des règles..."
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ban-duration">Durée du bannissement</Label>
              <Select value={banDuration} onValueChange={setBanDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 jour</SelectItem>
                  <SelectItem value="3">3 jours</SelectItem>
                  <SelectItem value="7">7 jours (1 semaine)</SelectItem>
                  <SelectItem value="14">14 jours (2 semaines)</SelectItem>
                  <SelectItem value="30">30 jours (1 mois)</SelectItem>
                  <SelectItem value="90">90 jours (3 mois)</SelectItem>
                  <SelectItem value="180">180 jours (6 mois)</SelectItem>
                  <SelectItem value="365">365 jours (1 an)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBanDialog(false)}
              disabled={isLoading}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleBanUser}
              disabled={isLoading}
            >
              {isLoading ? 'Bannissement...' : 'Bannir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModerationActions;
